// Guides IA — articles éditoriaux/réflexion (sans illustrations, focus sur le texte)

export const guides = [
  {
    slug: "qu-est-ce-que-l-intelligence-artificielle",
    title: "Qu'est-ce que l'intelligence artificielle ? Ce que vous devez vraiment savoir",
    seoTitle: "Qu'est-ce que l'intelligence artificielle ? | Guide IA — YAFORMIS",
    metaDescription:
      "Définition claire de l'intelligence artificielle : ce qu'elle est, comment elle fonctionne, ce qu'elle ne sait pas faire. Un guide accessible et rigoureux.",
    excerpt:
      "L'IA n'est pas une technologie unique mais un vaste ensemble de méthodes. Comprendre ce qu'elle est — et ce qu'elle n'est pas — est le point de départ de toute réflexion sérieuse.",
    readingTime: "6 min",
    publishedAt: "2025-12-10",
    category: "Fondamentaux",
    sections: [
      { type: "h2", text: "Un terme qui recouvre beaucoup de choses" },
      {
        type: "p",
        text: "L'intelligence artificielle n'est pas une technologie unique. C'est un vaste ensemble de méthodes permettant à des machines d'accomplir des tâches qui, jusqu'ici, nécessitaient l'intelligence humaine : comprendre un texte, reconnaître une image, traduire une langue, jouer aux échecs ou encore prédire la météo.",
      },
      {
        type: "p",
        text: "Le terme existe depuis 1956, date à laquelle des chercheurs américains ont posé les bases théoriques de ce domaine. Mais c'est seulement dans les années 2010, grâce à la puissance de calcul des ordinateurs modernes et à l'explosion des données disponibles, que l'IA a commencé à tenir ses promesses.",
      },
      { type: "h2", text: "Comment ça fonctionne, concrètement ?" },
      {
        type: "p",
        text: "La forme d'IA la plus répandue aujourd'hui s'appelle l'apprentissage automatique (ou machine learning). Au lieu de programmer chaque règle à la main, on montre à un système des milliers, parfois des millions d'exemples. Le système repère des régularités, affine sa compréhension par itérations successives, et finit par être capable de répondre à de nouvelles situations.",
      },
      {
        type: "p",
        text: "Un bon exemple : les filtres anti-spam. On n'a pas écrit de règle du type « si le mot 'héritage' apparaît trois fois, c'est un spam ». On a montré au système des millions de mails en lui disant lesquels étaient indésirables. Il a appris seul à les reconnaître.",
      },
      { type: "h2", text: "Ce que l'IA ne sait pas faire" },
      {
        type: "p",
        text: "Aussi impressionnante soit-elle, l'IA actuelle n'est pas dotée de conscience, d'émotions ou de jugement moral. Elle ne comprend pas au sens humain du terme : elle détecte des motifs dans des données. Elle peut se tromper lourdement sur des situations qu'elle n'a jamais rencontrées, et ne sait pas expliquer ses propres décisions.",
      },
      {
        type: "quote",
        text: "L'IA excelle là où les règles sont claires et les données abondantes. Elle tâtonne là où le contexte, l'éthique ou le bon sens sont nécessaires.",
      },
    ],
  },
  {
    slug: "ia-dans-votre-quotidien",
    title: "L'IA dans votre quotidien : vous l'utilisez déjà sans le savoir",
    seoTitle: "L'IA dans votre quotidien : usages invisibles et impact réel | YAFORMIS",
    metaDescription:
      "Santé, transports, éducation, banque… L'intelligence artificielle est partout, souvent invisible. Découvrez les cas d'usage qui changent vraiment les choses.",
    excerpt:
      "Sans que nous en ayons conscience, des dizaines de systèmes intelligents prennent chaque jour des micro-décisions à notre place. Tour d'horizon.",
    readingTime: "7 min",
    publishedAt: "2025-12-12",
    category: "Société",
    sections: [
      { type: "h2", text: "Le matin, dès le réveil" },
      {
        type: "p",
        text: "Votre alarme s'adapte à votre cycle de sommeil ? C'est de l'IA. Votre application de messagerie vous propose des réponses toutes faites ? C'est de l'IA. Votre réseau social vous montre en priorité les publications d'amis avec lesquels vous interagissez le plus ? Encore de l'IA.",
      },
      {
        type: "p",
        text: "Sans que nous en ayons conscience, des dizaines de systèmes intelligents prennent chaque jour des micro-décisions à notre place ou pour nous faciliter la vie.",
      },
      { type: "h2", text: "Les cas d'usage qui changent vraiment les choses" },
      {
        type: "p",
        text: "Dans le domaine de la santé, des algorithmes analysent des radios et des scanners avec une précision parfois supérieure à celle des radiologues pour détecter certains cancers à un stade précoce. Dans les transports, les systèmes d'aide à la conduite (freinage automatique, maintien dans la voie) sauvent des vies chaque année.",
      },
      {
        type: "p",
        text: "Dans l'éducation, des plateformes adaptatives ajustent le niveau des exercices en temps réel selon les difficultés de chaque élève. Dans la banque, des IA détectent les fraudes à la carte bancaire en quelques millisecondes, bien avant qu'un conseiller humain ne soit alerté.",
      },
      { type: "h2", text: "Une technologie invisible mais influente" },
      {
        type: "p",
        text: "La particularité de l'IA dans notre quotidien, c'est précisément qu'on ne la voit pas. Elle agit en coulisses, optimise, personnalise, filtre. Ce manque de visibilité pose des questions légitimes : qui décide de ce qu'on nous montre ? Sur quelle base nos données sont-elles utilisées ? Ces questions méritent d'être posées, même — et surtout — par les non-spécialistes.",
      },
    ],
  },
  {
    slug: "ia-et-emploi",
    title: "IA et emploi : la fin du travail ou une nouvelle révolution industrielle ?",
    seoTitle: "IA et emploi : impact sur le travail, métiers menacés ou transformés | YAFORMIS",
    metaDescription:
      "L'IA va-t-elle détruire l'emploi ? Que nous apprennent les révolutions industrielles passées ? Quelles tâches sont vraiment exposées ? Analyse.",
    excerpt:
      "L'histoire des révolutions technologiques nous offre un cadre précieux pour comprendre ce qui change vraiment — et ce qui mérite l'attention des politiques publiques.",
    readingTime: "8 min",
    publishedAt: "2025-12-14",
    category: "Société",
    sections: [
      { type: "h2", text: "Ce que l'histoire des révolutions technologiques nous apprend" },
      {
        type: "p",
        text: "À chaque grande vague technologique — la mécanisation au XIXᵉ siècle, l'automatisation dans les années 1970, l'informatisation dans les années 1990 — des pans entiers de métiers ont disparu. Des emplois de tisserands, d'opérateurs téléphoniques ou de dactylographes n'existent plus. Mais de nouveaux métiers, impensables auparavant, ont émergé.",
      },
      {
        type: "p",
        text: "Cela ne signifie pas que la transition est indolore. Elle ne l'a jamais été. Certains travailleurs ont souffert, certaines régions ont décliné. Mais à l'échelle des décennies, l'emploi total n'a pas reculé.",
      },
      { type: "h2", text: "Qu'est-ce que l'IA automatise vraiment ?" },
      {
        type: "p",
        text: "Les tâches les plus exposées sont celles qui sont répétitives, bien définies et qui s'appuient sur des données structurées : saisie comptable, rédaction de contrats standards, tri de documents, service client de premier niveau. Des professions entières vont être profondément transformées, même si elles ne disparaîtront pas forcément.",
      },
      {
        type: "p",
        text: "En revanche, les métiers qui nécessitent de la créativité, de l'empathie, du jugement contextuel ou une présence physique sont bien moins menacés à court terme. Un plombier, un infirmier, un avocat plaidant ou un instituteur ne seront pas remplacés demain.",
      },
      {
        type: "quote",
        text: "Ce ne sont pas les emplois qui disparaissent, mais les tâches. Le défi est d'accompagner ceux dont le travail change trop vite pour s'y adapter seuls.",
      },
      { type: "h2", text: "Quel rôle pour les politiques publiques ?" },
      {
        type: "p",
        text: "La vraie question n'est pas technologique, elle est sociale. Comment financer la reconversion professionnelle à grande échelle ? Comment faire en sorte que les gains de productivité liés à l'IA bénéficient au plus grand nombre, et pas seulement aux entreprises qui la déploient ?",
      },
      {
        type: "p",
        text: "Ces arbitrages dépendent de choix politiques, pas algorithmiques. L'IA ne décide pas de sa propre distribution. C'est là que le débat public a tout son rôle à jouer.",
      },
    ],
  },
  {
    slug: "biais-de-l-ia",
    title: "Les biais de l'IA : quand les algorithmes reproduisent nos inégalités",
    seoTitle: "Biais de l'IA : exemples, causes, comment les corriger | Guide YAFORMIS",
    metaDescription:
      "Pourquoi les algorithmes peuvent-ils discriminer ? Causes, exemples concrets et leviers de correction. Un guide pour comprendre l'éthique de l'IA.",
    excerpt:
      "Les algorithmes ne sont pas neutres par construction. Ils apprennent à partir de données qui reflètent le monde tel qu'il est — avec ses injustices.",
    readingTime: "7 min",
    publishedAt: "2025-12-16",
    category: "Éthique",
    sections: [
      { type: "h2", text: "Un exemple devenu célèbre" },
      {
        type: "p",
        text: "En 2018, Amazon a dû abandonner un outil de recrutement par IA après avoir découvert qu'il pénalisait systématiquement les candidatures féminines pour des postes techniques. Pourquoi ? Parce que le système avait été entraîné sur dix ans d'historique de recrutement — majoritairement masculin dans ce secteur. L'algorithme avait appris à reproduire ce que les humains avaient fait avant lui.",
      },
      { type: "h2", text: "Un biais qui vient des données, pas du code" },
      {
        type: "p",
        text: "Les algorithmes d'apprentissage automatique ne sont pas neutres par construction. Ils apprennent à partir de données existantes qui reflètent le monde tel qu'il est, avec ses injustices et ses déséquilibres. Un système de reconnaissance faciale entraîné principalement sur des visages caucasiens performera moins bien sur d'autres types de visages. Un algorithme de crédit calé sur des historiques discriminatoires reproduira ces discriminations.",
      },
      {
        type: "p",
        text: "Le problème n'est pas la malveillance des ingénieurs. C'est la structure même des données et la façon dont les systèmes les interprètent.",
      },
      {
        type: "quote",
        text: "Une IA non surveillée peut devenir le miroir grossissant de nos biais collectifs. La transparence et la diversité dans les équipes qui la conçoivent sont des garde-fous essentiels.",
      },
      { type: "h2", text: "Peut-on corriger ces biais ?" },
      {
        type: "p",
        text: "Oui, mais ce n'est pas simple. Il faut d'abord les identifier, ce qui nécessite des audits réguliers et des équipes pluridisciplinaires — des juristes, des sociologues, des spécialistes de l'éthique aux côtés des ingénieurs. Il faut ensuite choisir consciemment ce qu'on optimise : la performance globale peut masquer de mauvaises performances sur des sous-groupes de population.",
      },
      {
        type: "p",
        text: "Des régulations commencent à encadrer ces pratiques. En Europe, l'AI Act impose des obligations de transparence et de contrôle humain pour les systèmes à haut risque. C'est un premier pas important.",
      },
    ],
  },
  {
    slug: "ia-generale-science-ou-fiction",
    title: "Intelligence artificielle générale : science ou science-fiction ?",
    seoTitle: "AGI : qu'est-ce que l'intelligence artificielle générale ? | YAFORMIS",
    metaDescription:
      "L'IA générale (AGI) existe-t-elle déjà ? Pourquoi GPT-4 n'en est pas une ? Faut-il s'inquiéter ? Mise au point sans catastrophisme.",
    excerpt:
      "Aussi performantes soient-elles, les IA actuelles sont des spécialistes. L'AGI désigne autre chose. Et personne ne sait quand — ni si — elle adviendra.",
    readingTime: "7 min",
    publishedAt: "2025-12-18",
    category: "Prospective",
    sections: [
      { type: "h2", text: "L'IA d'aujourd'hui est « étroite »" },
      {
        type: "p",
        text: "Aussi performantes soient-elles, les IA actuelles sont des spécialistes. Un modèle de traduction ne sait pas jouer aux échecs. Un système de recommandation musicale ne sait pas reconnaître une tumeur. Chaque outil est entraîné pour une tâche précise, ou un ensemble de tâches connexes. On parle d'intelligence artificielle étroite, ou narrow AI.",
      },
      {
        type: "p",
        text: "L'intelligence artificielle générale (AGI, pour Artificial General Intelligence) désigne, elle, un système capable de raisonner et d'apprendre dans n'importe quel domaine, comme le fait un être humain. Ce stade n'est pas atteint — et les experts débattent vivement pour savoir si, et quand, il pourrait l'être.",
      },
      { type: "h2", text: "Les grandes IA génératives ne sont pas des AGI" },
      {
        type: "p",
        text: "Des modèles comme GPT-4 ou Gemini impressionnent par leur polyvalence apparente : ils rédigent, traduisent, codent, résument, expliquent. Mais ils ne comprennent pas. Ils prédisent la suite probable d'un texte à partir de milliards d'exemples. Ils peuvent produire des erreurs factuelles avec la même assurance que des vérités établies, ce qu'on appelle les « hallucinations ».",
      },
      {
        type: "p",
        text: "Leur impressionnante capacité à paraître intelligents masque une architecture fondamentalement différente de la pensée humaine.",
      },
      { type: "h2", text: "Une précaution raisonnable, sans panique inutile" },
      {
        type: "p",
        text: "Certains chercheurs très sérieux, dont des fondateurs de grandes entreprises d'IA, appellent à la prudence sur les risques à long terme d'une IA très puissante mal alignée avec les valeurs humaines. Ce débat mérite d'être pris au sérieux, sans pour autant verser dans le catastrophisme ou dans la fascination dystopique.",
      },
      {
        type: "p",
        text: "Le défi immédiat est moins spectaculaire mais plus concret : comment gouverner les IA que nous avons déjà, pour qu'elles servent l'intérêt général plutôt que des intérêts particuliers ? C'est là, aujourd'hui, que se joue l'essentiel.",
      },
      {
        type: "quote",
        text: "La vraie question n'est pas « l'IA va-t-elle dominer le monde ? », mais « qui décide de comment elle est utilisée, et avec quels garde-fous ? »",
      },
    ],
  },
];

export const getGuideBySlug = (slug) => guides.find((g) => g.slug === slug);
